/**
 * CompanyDetailPage — single-company view: header (name/domain/industry/
 * website/description), contacts roll-up, recent activity timeline,
 * tags editor (canonical lowercase). Edit-in-place via the patch hook.
 */

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Archive } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import {
  useFiasLeadsCompany,
  useUpdateFiasLeadsCompany,
  useArchiveFiasLeadsCompany,
  useFiasLeadsContacts,
} from '../hooks/useFiasLeads';
import { SkeletonBlock } from '../components/Skeletons';
import { ActivitiesTimelinePanel } from '../components/ActivitiesTimelinePanel';
import { useConfirm } from '@/components/ui/confirm-dialog';

interface Props {
  holderId: string;
}

export function CompanyDetailPage({ holderId }: Props) {
  const confirm = useConfirm();
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const companyQuery = useFiasLeadsCompany(holderId, companyId ?? null);
  const updateCompany = useUpdateFiasLeadsCompany(holderId);
  const archiveCompany = useArchiveFiasLeadsCompany(holderId);
  const contactsQuery = useFiasLeadsContacts(holderId, { companyId });

  const [edit, setEdit] = useState({
    name: '',
    domain: '',
    industry: '',
    website: '',
    description: '',
  });
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (companyQuery.data) {
      setEdit({
        name: companyQuery.data.name,
        domain: companyQuery.data.domain ?? '',
        industry: companyQuery.data.industry ?? '',
        website: companyQuery.data.website ?? '',
        description: companyQuery.data.description ?? '',
      });
      setIsDirty(false);
    }
  }, [companyQuery.data]);

  const handleSave = async () => {
    if (!companyId) return;
    await updateCompany.mutateAsync({
      companyId,
      input: {
        name: edit.name,
        domain: edit.domain || null,
        industry: edit.industry || null,
        website: edit.website || null,
        description: edit.description || null,
      },
    });
    setIsDirty(false);
  };

  const handleArchive = async () => {
    if (!companyId) return;
    if (
      !(await confirm({
        title: 'Archive this company?',
        description: 'Contacts and deals stay; the company moves to archived.',
        confirmLabel: 'Archive company',
        destructive: true,
      }))
    ) {
      return;
    }
    await archiveCompany.mutateAsync(companyId);
    navigate(`${ARCHE_PATH}/companies`);
  };

  if (companyQuery.isLoading) return <SkeletonBlock lines={6} />;
  if (companyQuery.isError || !companyQuery.data) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Link
          to={`${ARCHE_PATH}/companies`}
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Companies
        </Link>
        <p className="mt-4 text-destructive">Company not found.</p>
      </div>
    );
  }

  const c = companyQuery.data;
  const contacts = contactsQuery.data ?? [];

  return (
    <div className="p-6 max-w-5xl mx-auto flex flex-col gap-6">
      <Link
        to={`${ARCHE_PATH}/companies`}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline w-fit"
      >
        <ArrowLeft className="h-4 w-4" />
        Companies
      </Link>

      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{c.name}</h1>
          <p className="text-sm text-muted-foreground">
            {c.industry ?? '—'} {c.domain ? `· ${c.domain}` : ''}
          </p>
        </div>
        <button
          onClick={handleArchive}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive"
          disabled={archiveCompany.isPending}
        >
          <Archive className="h-4 w-4" />
          Archive
        </button>
      </header>

      <div className="rounded border border-border bg-card p-4">
        <h2 className="text-sm font-medium text-muted-foreground mb-3">Details</h2>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Name"
            value={edit.name}
            onChange={(v) => {
              setEdit((p) => ({ ...p, name: v }));
              setIsDirty(true);
            }}
          />
          <Field
            label="Domain"
            value={edit.domain}
            onChange={(v) => {
              setEdit((p) => ({ ...p, domain: v }));
              setIsDirty(true);
            }}
          />
          <Field
            label="Industry"
            value={edit.industry}
            onChange={(v) => {
              setEdit((p) => ({ ...p, industry: v }));
              setIsDirty(true);
            }}
          />
          <Field
            label="Website"
            value={edit.website}
            onChange={(v) => {
              setEdit((p) => ({ ...p, website: v }));
              setIsDirty(true);
            }}
          />
          <div className="col-span-2">
            <label className="text-xs text-muted-foreground">Description</label>
            <textarea
              value={edit.description}
              onChange={(e) => {
                setEdit((p) => ({ ...p, description: e.target.value }));
                setIsDirty(true);
              }}
              rows={3}
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
        </div>
        {isDirty && (
          <button
            onClick={handleSave}
            disabled={updateCompany.isPending}
            className="mt-3 rounded bg-primary px-3 py-1.5 text-primary-foreground text-sm"
          >
            {updateCompany.isPending ? 'Saving…' : 'Save changes'}
          </button>
        )}
      </div>

      <div className="rounded border border-border bg-card p-4">
        <h2 className="text-sm font-medium text-muted-foreground mb-3">
          Contacts ({contacts.length})
        </h2>
        {contacts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No contacts linked yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {contacts.map((ct) => (
              <li key={ct.contactId} className="py-2">
                <Link
                  to={`${ARCHE_PATH}/contacts/${ct.contactId}`}
                  className="text-sm text-foreground hover:underline"
                >
                  {ct.firstName} {ct.lastName ?? ''}
                </Link>
                <span className="ml-2 text-xs text-muted-foreground">
                  {ct.primaryEmail ?? ct.title ?? ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ActivitiesTimelinePanel
        holderId={holderId}
        regardingType="vault_company"
        regardingId={companyId!}
      />
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-border bg-background px-3 py-2 text-sm"
      />
    </label>
  );
}
